/**
 * The player-prop synthetic-key contract (#566).
 *
 * Player-prop rows are not real games, so the views mint a synthetic GameRow key
 * for them:
 *
 *     <gameKeyV2>|prop-<stat>-<playerKey>-<threshold>     a rung  (one market)
 *     <gameKeyV2>|prop-<stat>-<playerKey>                 a group (one ladder)
 *
 * Before this module the marker literal was written out by hand in four files —
 * the Position Tracker, GameDetail's summary rows, the order-ticket label, and
 * analytics base-game recovery — and only one of them named it. The #484 audit
 * flagged the drift risk: the two consumers that matter fail in opposite ways.
 * A label matcher that stops recognising the marker falls through to 'moneyline'
 * and describes a strikeouts prop with the parent game's team codes (the #251
 * defect, already shipped once); base-game recovery that stops recognising it
 * fails CLOSED and disables per-game bulk cancel rather than aiming it at the
 * wrong game.
 *
 * ── The marker is `|prop-`, with a HYPHEN ──────────────────────────────────
 * Its three neighbours (`|spread|`, `|total|`, `|multi|`) use `|kind|`, so
 * "tidying" this one to match is the natural instinct and it is silent: a
 * matcher written for `|prop|` type-checks, reads correctly, and never fires.
 * The trailing hyphen is also load-bearing for `baseGameKey` — it is what stops
 * an unrelated `|property…` segment being stripped and collapsing a non-game key
 * onto a real game, which would point a DESTRUCTIVE cancel at the wrong scope
 * (the #272 note in services/analyticsGameKey.ts).
 *
 * ── Recognising and PARSING are deliberately different strengths ───────────
 * This asymmetry is existing shipped behavior, verified against the pre-#566
 * code, and centralizing must not collapse it:
 *
 *   `hasPropSynthKey`  — marker presence only. What the order-ticket label and
 *                        base-game recovery ask. `…|prop-nosuchstat-x-1` IS a
 *                        prop to them: the row is not a moneyline, and its base
 *                        game is still recoverable.
 *   `parsePropSynthKey` — strict. Rejects an unknown stat or a non-numeric
 *                        threshold, so a stray key can never be READ as a prop
 *                        and re-derived into a live market.
 *
 * Making the label check strict would relabel malformed prop rows as moneylines
 * — the exact #251 mislabel this contract exists to prevent.
 */
import { PROP_STATS, type PropStat } from './propStat';

/** The one marker. `|prop-`, not `|prop|` — see the header. */
export const PROP_SYNTH_MARKER = '|prop-';

/** A prop synth key read back into its parts. */
export interface ParsedPropSynthKey {
  /** The parent game's v2 key — the doubleheader-safe event identity. */
  gameKey: string;
  stat: PropStat;
  playerKey: string;
  threshold: number;
}

/** A ladder's stable identity: one player, one stat, every threshold. */
export function propGroupKey(gameKey: string, stat: PropStat, playerKey: string): string {
  return `${gameKey}${PROP_SYNTH_MARKER}${stat}-${playerKey}`;
}

/** One rung of a ladder: a single binary market. */
export function propRungKey(
  gameKey: string,
  stat: PropStat,
  playerKey: string,
  threshold: number,
): string {
  return `${propGroupKey(gameKey, stat, playerKey)}-${threshold}`;
}

/** Append a threshold to an already-built group key. Same string as
 *  `propRungKey`, for callers that already hold the group key and store it
 *  alongside the rung (GameDetail's summary rows carry both). */
export function propRungKeyOf(groupKey: string, threshold: number): string {
  return `${groupKey}-${threshold}`;
}

/**
 * Does this key name a player prop? Marker presence only — see the header on why
 * this is deliberately weaker than `parsePropSynthKey`.
 */
export function hasPropSynthKey(key: string | null | undefined): boolean {
  return typeof key === 'string' && key.includes(PROP_SYNTH_MARKER);
}

/**
 * Read a prop synth key back into its parts, or null.
 *
 * The stat is matched as a KNOWN id followed by a hyphen, longest id first —
 * NFL's ids are themselves hyphenated (`rush-rec-yds`, `first-td`; the MLB
 * eight are not), so a split on `-` cannot find the stat boundary on its own.
 * The threshold is the numeric last segment, and the middle segments rejoin
 * to the playerKey — which DOES take hyphens on a Poly-only ladder
 * ('aj-ewing'). Fail-closed on anything else: an unknown stat or a
 * non-numeric threshold returns null rather than a half-read prop.
 *
 * KNOWN AMBIGUITY, pre-existing and deliberately preserved by #566 (which was
 * fenced to "no change to any existing key string"). A playerKey may END IN A
 * DIGIT SEGMENT — `slugifyPlayerName` keeps `[a-z0-9]` and Poly's slug pattern
 * allows digit segments — and the threshold is also the last segment, so a
 * GROUP key for `luis-garcia-2` is byte-identical to the RUNG key for
 * `luis-garcia` at threshold 2, and this reads it as the latter:
 *
 *     propGroupKey(gk, 'ks', 'luis-garcia-2') === propRungKey(gk, 'ks', 'luis-garcia', 2)
 *
 * Rung keys for such a player are unaffected (their own threshold is last).
 * Fixing it means changing the minted format — a separate ticket, and this
 * module is now the one place that change would land.
 */
export function parsePropSynthKey(key: string): ParsedPropSynthKey | null {
  const i = key.indexOf(PROP_SYNTH_MARKER);
  if (i < 0) return null;
  const tail = key.slice(i + PROP_SYNTH_MARKER.length);
  const stat = STATS_LONGEST_FIRST.find((s) => tail.startsWith(`${s}-`));
  if (!stat) return null;
  const parts = tail.slice(stat.length + 1).split('-');
  if (parts.length < 2) return null;
  const threshold = Number(parts[parts.length - 1]);
  if (!Number.isFinite(threshold)) return null;
  const playerKey = parts.slice(0, -1).join('-');
  return playerKey ? { gameKey: key.slice(0, i), stat, playerKey, threshold } : null;
}

/** Longest id first so `rush-rec-yds-` is tried before `rec-` (and `rec-yds-`
 *  before `rec-`): a shorter id that prefixes a longer one would otherwise
 *  swallow the first segment of the longer id's playerKey. */
const STATS_LONGEST_FIRST: readonly PropStat[] = [...PROP_STATS].sort(
  (a, b) => b.length - a.length || a.localeCompare(b),
);
